import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, ButtonGroup, useTheme, useMediaQuery, Card, CardContent, CardActions, Stack, Divider, Box, Chip } from '@mui/material';
import { useApplicationContext } from 'ApplicationContext';
import { DeviceQueryModel, GetSupportCodeData, DeviceStatusEnum, SetDeviceStatusModel } from 'apiClient/Api';
import { useParams } from 'react-router';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';

/**
 * DeviceManagement component
 * @returns {JSX.Element} The DeviceManagement page
 */
const DeviceManagement: React.FC = (): JSX.Element => {

  useDocumentTitle("base.pages.deviceManagement")
  const context = useApplicationContext();
  const controller = context.api.device;
  const supportCodeController = context.api.supportCode;

  const { t } = useTranslation();
  const [devices, setDevices] = React.useState<DeviceQueryModel[]>([]);
  const [supportCode, setSupportCode] = React.useState<GetSupportCodeData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { supportId } = useParams<{ supportId?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * Updates the data by fetching devices and support code if available
   */
  const updateData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceData = await controller.deviceGetDevices({ supportCodeId: supportId });
      if (deviceData && deviceData.data) {
        setDevices(deviceData.data);
      }

      if (supportId) {
        const supportCodeData = await supportCodeController.supportCodeGetSupportCode(supportId);
        if (supportCodeData && supportCodeData.data) {
          setSupportCode(supportCodeData.data);
        } else {
          setError(t('error.supportCodeNotFound'));
        }
      } else {
        setSupportCode(null);
      }
    } catch (err) {
      setError(t('error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [supportId, t, controller, supportCodeController]);

  React.useEffect(() => {
    updateData();
  }, [supportId, updateData]);

  /**
   * Handles the status change of a device
   * @param {string} deviceId - The ID of the device
   * @param {DeviceStatusEnum} newStatus - The new status to set
   */
  const handleStatusChange = async (deviceId: string, newStatus: DeviceStatusEnum) => {
    try {
      const data: SetDeviceStatusModel = { deviceId, newStatus };
      await controller.deviceSetDeviceStatus(data);
      updateData();
    } catch (err) {
      setError(t('error.fetchFailed'));
    }
  };

  /**
   * Renders action buttons based on device status
   * @param {DeviceStatusEnum} status - The status of the device
   * @param {string} deviceId - The ID of the device
   * @returns {JSX.Element} The action buttons
   */
  const renderActionButtons = (status?: DeviceStatusEnum, deviceId?: string): JSX.Element => {
    switch (status) {
      case DeviceStatusEnum.Pending:
        return (
          <ButtonGroup>
            <Button variant="contained" color="primary" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Active)}>
              {t('deviceManagement.actions.activate')}
            </Button>
            <Button variant="contained" color="secondary" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Inactive)}>
              {t('deviceManagement.actions.revoke')}
            </Button>
          </ButtonGroup>
        );
      case DeviceStatusEnum.Active:
        return (
          <Button variant="contained" color="secondary" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Inactive)}>
            {t('deviceManagement.actions.revoke')}
          </Button>
        );
      case DeviceStatusEnum.Inactive:
        return (

          <ButtonGroup>
            <Button variant="contained" color="primary" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Active)}>
              {t('deviceManagement.actions.activate')}
            </Button>
            <Button variant="contained" color="error" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Deleted)}>
            {t('deviceManagement.actions.delete')}
            </Button>
          </ButtonGroup>
        );
      default:
        return <></>;
    }
  };

  /**
   * Gets the color for status chip based on device status
   */
  const getStatusColor = (status?: DeviceStatusEnum): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case DeviceStatusEnum.Active:
        return 'success';
      case DeviceStatusEnum.Pending:
        return 'warning';
      case DeviceStatusEnum.Inactive:
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Renders mobile card view for devices
   */
  const renderMobileView = () => {
    if (devices.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('deviceManagement.noDevices') || 'Keine Geräte gefunden'}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ px: 2, mt: 2 }}>
        {devices.map((device) => (
          <Card key={device.id}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {device.hostSystemName}
                  </Typography>
                  <Chip
                    label={t(`deviceManagement.status.${device.deviceStatus}`)}
                    size="small"
                    color={getStatusColor(device.deviceStatus)}
                  />
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.id')}:
                    </Typography>
                    <Typography variant="body2">
                      {device.id}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.deviceId')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                      {device.deviceId}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.osVersion')}:
                    </Typography>
                    <Typography variant="body2">
                      {device.osVersion}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.supportCode')}:
                    </Typography>
                    <Typography variant="body2">
                      {device.supportCode}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
            {device.deviceStatus !== DeviceStatusEnum.Deleted && (
              <CardActions>
                <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                  {device.deviceStatus === DeviceStatusEnum.Pending && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleStatusChange(device.deviceId!, DeviceStatusEnum.Active)}
                      >
                        {t('deviceManagement.actions.activate')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleStatusChange(device.deviceId!, DeviceStatusEnum.Inactive)}
                      >
                        {t('deviceManagement.actions.revoke')}
                      </Button>
                    </>
                  )}
                  {device.deviceStatus === DeviceStatusEnum.Active && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => handleStatusChange(device.deviceId!, DeviceStatusEnum.Inactive)}
                    >
                      {t('deviceManagement.actions.revoke')}
                    </Button>
                  )}
                  {device.deviceStatus === DeviceStatusEnum.Inactive && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleStatusChange(device.deviceId!, DeviceStatusEnum.Active)}
                      >
                        {t('deviceManagement.actions.activate')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleStatusChange(device.deviceId!, DeviceStatusEnum.Deleted)}
                      >
                        {t('deviceManagement.actions.delete')}
                      </Button>
                    </>
                  )}
                </Stack>
              </CardActions>
            )}
          </Card>
        ))}
      </Stack>
    );
  };

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Typography variant="h4" component={"h1"} gutterBottom>{t('deviceManagement.title')}</Typography>
        {loading ? <Typography>{t('loading')}</Typography> : (
          <>
            {supportCode && (
              <div>
                <Typography variant="h6">{t('supportCode.id')}: {supportCode.id}</Typography>
                <Typography variant="h6">{t('supportCode.name')}: {supportCode.name}</Typography>
              </div>
            )}
            {isMobile ? (
              renderMobileView()
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('deviceManagement.columns.id')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.deviceId')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.hostSystemName')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.osVersion')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.supportCode')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.deviceStatus')}</TableCell>
                      <TableCell>{t('deviceManagement.columns.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>{device.id}</TableCell>
                        <TableCell>{device.deviceId}</TableCell>
                        <TableCell>{device.hostSystemName}</TableCell>
                        <TableCell>{device.osVersion}</TableCell>
                        <TableCell>{device.supportCode}</TableCell>
                        <TableCell>{t(`deviceManagement.status.${device.deviceStatus}`)}</TableCell>
                        <TableCell>{renderActionButtons(device.deviceStatus, device.deviceId)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
        {error && <Typography color="error">{error}</Typography>}
    </Container>
  );
};

export default DeviceManagement;
